import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, TrendingUp, AlertTriangle } from "lucide-react";

type Bucket = { dollars: number; count: number };

type CashFlowResponse = {
  buckets: { overdue: Bucket; d30: Bucket; d60: Bucket; d90: Bucket };
  totalProjected: number;
  overdueDollars: number;
  regions: Array<{ regionId: string | null; name: string; dollars: number; count: number }>;
  topClients: Array<{ clientId: string; accountName: string; dollars: number; count: number }>;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function BucketCard({ label, bucket, tone = "default" }: { label: string; bucket: Bucket; tone?: "default" | "warn" }) {
  return (
    <Card data-testid={`bucket-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase text-muted-foreground flex items-center gap-2">
          {tone === "warn" && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-semibold tabular-nums ${tone === "warn" ? "text-amber-600 dark:text-amber-400" : ""}`}>
          {fmtMoney(bucket.dollars)}
        </div>
        <div className="text-xs text-muted-foreground">{bucket.count} invoice{bucket.count === 1 ? "" : "s"}</div>
      </CardContent>
    </Card>
  );
}

export function CashFlowTab() {
  const { data, isLoading } = useQuery<CashFlowResponse>({ queryKey: ["/api/billing/cash-flow"] });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="tab-cash-flow">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BucketCard label="Overdue" bucket={data.buckets.overdue} tone="warn" />
        <BucketCard label="Next 30 days" bucket={data.buckets.d30} />
        <BucketCard label="31–60 days" bucket={data.buckets.d60} />
        <BucketCard label="61–90 days" bucket={data.buckets.d90} />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Projected inflow (next 90 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold tabular-nums" data-testid="text-total-projected">
            {fmtMoney(data.totalProjected)}
          </div>
          {data.overdueDollars > 0 && (
            <div className="text-sm text-amber-600 dark:text-amber-400 mt-1">
              + {fmtMoney(data.overdueDollars)} overdue still outstanding
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By region</CardTitle>
          </CardHeader>
          <CardContent>
            {data.regions.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No projected inflow.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Region</TableHead>
                    <TableHead className="text-right">Invoices</TableHead>
                    <TableHead className="text-right">Projected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.regions.map((r) => (
                    <TableRow key={r.regionId ?? "_none"} data-testid={`row-region-${r.regionId ?? "none"}`}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtMoney(r.dollars)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top clients (by projected inflow)</CardTitle>
          </CardHeader>
          <CardContent>
            {data.topClients.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No projected inflow.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Invoices</TableHead>
                    <TableHead className="text-right">Projected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.topClients.map((c) => (
                    <TableRow key={c.clientId} data-testid={`row-client-${c.clientId}`}>
                      <TableCell className="font-medium truncate max-w-[260px]">{c.accountName}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.count}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMoney(c.dollars)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
